import { Component, lazy, PureComponent, type ComponentType, type LazyExoticComponent } from "react"

import { isNil } from "./any"
import { ReactComponent } from "~/src/customElements/reactComponent"

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __reactComponents: Record<string, AnyComponent<any>>
  }
}

export type AnyComponent<P extends object> = ComponentType<P> | LazyExoticComponent<ComponentType<P>>

type ComponentLoader = () => Promise<unknown>

/** Each loader runs only when a `<react-component>` naming it first renders. */
export function registerComponents(loaders: Record<string, ComponentLoader>) {
  window.__reactComponents ||= {}

  for (const [name, load] of Object.entries(loaders)) {
    window.__reactComponents[name] = lazyLoad(name, load)
  }

  if (!customElements.get("react-component")) {
    customElements.define("react-component", ReactComponent)
  }
}

export function getComponent<P extends object>(name: string): AnyComponent<P> {
  const component = window.__reactComponents[name]

  if (isNil(component)) throw new Error(`${name} component must be registered`)

  return component
}

//region Private
function lazyLoad(name: string, load: ComponentLoader) {
  return lazy(() =>
    load().then((module) => {
      if (!isRecord(module)) {
        throw new TypeError(`Expected '${name}' to be a dynamic import`)
      }

      const component = module?.default ?? module[name]

      if (isComponent(component)) {
        return { default: component }
      } else {
        throw new TypeError(`Could not find '${name}' a dynamically imported React component`)
      }
    })
  )
}

function isComponent(value: unknown): value is ComponentType {
  return typeof value === "function" || value instanceof Component || value instanceof PureComponent
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}
//endregion
